import SettingsIcon from "@mui/icons-material/Settings";
import { Box, Chip, Skeleton, Typography } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useExpandedGroups } from "../../../hooks/useExpandedGroups";
import { AlertFilterBar } from "./AlertFilterBar";
import { DateSection } from "./DateSection";
import { SeverityConfigPanel } from "./SeverityConfigPanel";
import type { AlertSeverity, AlertSortMode, AlertType, DepartmentAlertDayView, DepartmentAlertsResponse } from "./types";
import {
	ALERT_TYPE_COLORS,
	ALERT_TYPE_I18N_KEYS,
	ALL_ALERT_TYPES,
	computeAlertCounts,
	computeTotalAlertTypeCounts,
	mergeProblemsAlerts,
	SORT_COMPARATORS,
} from "./utils";

type Props = {
	data: DepartmentAlertsResponse | undefined;
	isLoading: boolean;
};

export const DepartmentAlerts: FC<Props> = ({ data, isLoading }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const [configOpen, setConfigOpen] = useState(false);
	const [selectedTypes, setSelectedTypes] = useState<Set<AlertType>>(new Set());
	const [selectedSeverities, setSelectedSeverities] = useState<Set<AlertSeverity>>(new Set());
	const [sortMode, setSortMode] = useState<AlertSortMode>("newest");
	const [showPerTypeCount, setShowPerTypeCount] = useState(false);
	const [showProblems, setShowProblems] = useState(false);
	const { expandedGroups: expandedDates, toggleGroupExpand: toggleDate } = useExpandedGroups();

	const showAll = selectedTypes.size === 0;

	const toggleType = useCallback(
		(type: AlertType) => {
			track("department_alerts_filter_type_toggled", { alert_type: type });
			setSelectedTypes((prev) => {
				const next = new Set(prev);
				if (next.has(type)) next.delete(type);
				else next.add(type);
				return next;
			});
		},
		[track],
	);

	const toggleSeverity = useCallback(
		(sev: AlertSeverity) => {
			track("department_alerts_severity_toggled", { severity: sev });
			setSelectedSeverities((prev) => {
				const next = new Set(prev);
				if (next.has(sev)) next.delete(sev);
				else next.add(sev);
				return next;
			});
		},
		[track],
	);

	// "All" resets both filter dimensions so every alert is visible
	const toggleAll = useCallback(() => {
		track("department_alerts_filter_reset");
		setSelectedTypes(new Set());
		setSelectedSeverities(new Set());
	}, [track]);

	const handleSortChange = useCallback(
		(mode: AlertSortMode) => {
			track("department_alerts_sort_changed", { sort_mode: mode });
			setSortMode(mode);
		},
		[track],
	);

	// Deep-clone the day tree when filtering so we don't mutate TanStack Query's cached data.
	// Each level (day → shiftType → roleGroup → alerts) is spread into a new object.
	const filteredDays = useMemo((): DepartmentAlertDayView[] => {
		if (!data?.days) return [];
		let days = [...data.days];

		if (selectedTypes.size > 0 || selectedSeverities.size > 0) {
			days = days
				.map((day) => {
					const filteredShiftTypes = day.shiftTypes
						.map((st) => ({
							...st,
							roleGroups: st.roleGroups
								.map((rg) => ({
									...rg,
									alerts: rg.alerts.filter((a) => {
										const typeMatch = selectedTypes.size === 0 || selectedTypes.has(a.alertType as AlertType);
										const sevMatch = selectedSeverities.size === 0 || (a.severity && selectedSeverities.has(a.severity as AlertSeverity));
										return typeMatch && sevMatch;
									}),
								}))
								.filter((rg) => rg.alerts.length > 0),
						}))
						.filter((st) => st.roleGroups.length > 0);

					const counts = computeAlertCounts(filteredShiftTypes);
					return { ...day, shiftTypes: filteredShiftTypes, ...counts };
				})
				.filter((day) => day.totalAlerts > 0);
		}

		days.sort(SORT_COMPARATORS[sortMode]);

		if (showProblems) {
			return days.map((day) => ({
				...day,
				shiftTypes: day.shiftTypes.map((st) => ({
					...st,
					roleGroups: st.roleGroups.map((rg) => mergeProblemsAlerts(rg)),
				})),
			}));
		}

		return days;
	}, [data, selectedTypes, selectedSeverities, sortMode, showProblems]);

	const handleToggleConfig = useCallback(() => {
		setConfigOpen((p) => {
			const next = !p;
			track("department_alerts_config_toggled", { open: next });
			return next;
		});
	}, [track]);

	const handleTogglePerTypeCount = useCallback(() => {
		track("department_alerts_per_type_count_toggled");
		setShowPerTypeCount((p) => !p);
	}, [track]);

	const handleToggleProblems = useCallback(() => {
		track("department_alerts_show_problems_toggled");
		setShowProblems((p) => !p);
	}, [track]);

	const totalAlertsByType = useMemo(() => computeTotalAlertTypeCounts(data?.days ?? []), [data]);

	const defaultRules = useMemo(() => data?.defaultRules ?? [], [data?.defaultRules]);

	if (isLoading || !data)
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					py: 2,
				}}
			>
				<Skeleton variant="rounded" height={40} />
				<Skeleton variant="rounded" height={40} />
				<Skeleton variant="rounded" height={40} />
			</Box>
		);
	if (data.days.length === 0) return null;

	return (
		<CommonDashboardBox sx={{ width: "100%", mt: 3 }}>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mb: 1,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						color: "text.secondary",
					}}
				>
					{t("subtitle")}
				</Typography>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					{/* Summary chips */}
					{ALL_ALERT_TYPES.map((type) => {
						const count = totalAlertsByType[type] ?? 0;
						if (count === 0) return null;
						const colors = ALERT_TYPE_COLORS[type];
						return (
							<Chip
								key={type}
								size="small"
								label={
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
										}}
									>
										<Typography component="span" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
											{count}
										</Typography>
										<Typography component="span" sx={{ fontSize: "0.7rem" }}>
											{t(ALERT_TYPE_I18N_KEYS[type])}
										</Typography>
									</Box>
								}
								sx={{ bgcolor: colors.chipBg, color: colors.text }}
							/>
						);
					})}

					<SystemButton
						buttonVariant="borderless"
						size="small"
						leadingIcon={<SettingsIcon fontSize="small" />}
						onClick={handleToggleConfig}
						sx={{ bgcolor: configOpen ? "grey.200" : "transparent" }}
					/>
				</Box>
			</Box>
			{/* Severity Config Panel */}
			<SeverityConfigPanel open={configOpen} onClose={() => setConfigOpen(false)} rules={defaultRules} />
			{/* Filter Bar */}
			<AlertFilterBar
				data={data}
				selectedTypes={selectedTypes}
				onToggleType={toggleType}
				selectedSeverities={selectedSeverities}
				onToggleSeverity={toggleSeverity}
				sortMode={sortMode}
				onSortChange={handleSortChange}
				showAll={showAll}
				onToggleAll={toggleAll}
				showPerTypeCount={showPerTypeCount}
				onTogglePerTypeCount={handleTogglePerTypeCount}
				showProblems={showProblems}
				onToggleProblems={handleToggleProblems}
			/>
			{/* Date Sections */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					mt: 2,
				}}
			>
				{filteredDays.length === 0 && (
					<Typography
						variant="body2"
						sx={{
							color: "text.secondary",
							textAlign: "center",
							py: 3,
						}}
					>
						{t("no.alerts")}
					</Typography>
				)}

				{filteredDays.map((day) => (
					<DateSection
						key={day.date.toISODate()}
						day={day}
						isExpanded={expandedDates.has(day.date.toISODate() ?? "")}
						onToggle={toggleDate}
						showPerTypeCount={showPerTypeCount}
					/>
				))}
			</Box>
		</CommonDashboardBox>
	);
};
